// interview-store.ts — B4 (plan 20260915-1650 phase-04).
//
// Nơi câu trả lời của NGƯỜI được lưu bền, và nơi gate dispatch hỏi "còn câu
// required nào chưa trả lời không".
//
// Vì sao phải có file riêng, không nhét vào directive payload: đo verbatim
// 2026-09-16 cho thấy đường `answer` hiện tại NUỐT câu trả lời —
// `mattermost-command-body-parser.ts` dựng `{ticket, request_id, text}`,
// `supervisor-directive-processing.ts` khi đó xếp `answer` chung
// `REDIRECT_LIKE_KINDS` (P4 đã tách ra, xem
// `interview-answer-directive-processing.ts`),
// rồi `applyRedirectDirective` chỉ đọc `proposed_changes`/
// `supersede_done_ticket_ids`/`invalidate_gate_ids`. Không field nào nhận `text`
// ⇒ directive ghi bền, đánh dấu processed, nội dung biến mất. Gate mà không có
// store này thì chặn vĩnh viễn (không đường nào ghi được câu trả lời).
//
// KHÔNG có OCC ở đây (khác gates.json): writer là directive-processing chạy ở
// TICK BOUNDARY, một tiến trình, tuần tự — chưa có cặp writer đồng thời thật.
// Thêm OCC lúc chưa có writer thứ hai là lớp thừa (YAGNI); khi nào có, khuôn
// `GateFile.revision` (P2/B1) đã sẵn để bê sang.
import { join } from "node:path";
import { pathExists, readJson } from "../filesystem-helpers.js";
import { writeFileDurable } from "./durable-file-writer.js";
import { resolveAnswerValues } from "./interview-answer-option-id-resolution.js";
import {
  type Interview,
  type InterviewQuestion,
  InterviewSchema,
} from "./schemas/interview-schema.js";

export type { Interview, InterviewQuestion } from "./schemas/interview-schema.js";

/** Người vận hành mở file này bằng tay để đọc câu hỏi — tên là hợp đồng, khoá bằng test. */
export const INTERVIEW_FILE_NAME = "interview.json";

function interviewPath(controlRoot: string): string {
  return join(controlRoot, INTERVIEW_FILE_NAME);
}

/**
 * Đọc interview.json. Trả `undefined` khi mission KHÔNG có file (đa số mission,
 * gồm cả 6 ticket fixture e2e) hoặc file hỏng — CẢ HAI ca đều KHÔNG được chặn
 * dispatch, xem `requireAnsweredQuestionIds`.
 */
export async function readInterview(controlRoot: string): Promise<Interview | undefined> {
  const path = interviewPath(controlRoot);
  if (!(await pathExists(path))) return undefined;
  try {
    const raw = await readJson<unknown>(path);
    const parsed = InterviewSchema.safeParse(raw);
    return parsed.success ? parsed.data : undefined;
  } catch {
    return undefined;
  }
}

async function writeInterview(controlRoot: string, interview: Interview): Promise<void> {
  await writeFileDurable(
    interviewPath(controlRoot),
    Buffer.from(`${JSON.stringify(interview, null, 2)}\n`, "utf8"),
  );
}

export interface WriteInterviewQuestionsInput {
  controlRoot: string;
  questions: InterviewQuestion[];
  now?: () => Date;
}

/**
 * Ghi bộ câu hỏi. Câu trả lời ĐÃ CÓ cho id vẫn còn trong bộ mới được GIỮ LẠI —
 * hỏi lại vòng hai (clarify-questions.md cho phép 2 lượt) không được xoá công
 * người đã trả lời ở lượt một.
 */
export async function writeInterviewQuestions(
  input: WriteInterviewQuestionsInput,
): Promise<Interview> {
  const now = input.now ?? (() => new Date());
  const existing = await readInterview(input.controlRoot);
  const keptIds = new Set(input.questions.map((q) => q.id));
  const answers = Object.fromEntries(
    Object.entries(existing?.answers ?? {}).filter(([id]) => keptIds.has(id)),
  );

  const interview = InterviewSchema.parse({
    schema_version: 1,
    asked_at: existing?.asked_at ?? now().toISOString(),
    questions: input.questions,
    answers,
  });
  await writeInterview(input.controlRoot, interview);
  return interview;
}

export interface RecordInterviewAnswerInput {
  controlRoot: string;
  questionId: string;
  answer: string | string[];
  answeredBy: string;
  now?: () => Date;
}

export type RecordInterviewAnswerResult = { ok: true } | { ok: false; reason: string };

/**
 * Lưu một câu trả lời. Từ chối (KHÔNG ghi gì) khi:
 *  - chưa có interview.json — không có câu hỏi nào thì không có gì để trả lời;
 *  - `questionId` không nằm trong bộ câu hỏi đã hỏi (chống trả lời id bịa);
 *  - giá trị nằm ngoài `options` đã khai (options ràng buộc, không trang trí).
 * Câu có `option_ids` (V2-1) lưu option id — nhãn duy nhất được chuẩn hoá về id.
 * `answeredBy` = actor của directive, hoặc `delegated:<G0 id>` khi controller tự áp.
 */
export async function recordInterviewAnswer(
  input: RecordInterviewAnswerInput,
): Promise<RecordInterviewAnswerResult> {
  const now = input.now ?? (() => new Date());
  const interview = await readInterview(input.controlRoot);
  if (interview === undefined) {
    return { ok: false, reason: `chưa có ${INTERVIEW_FILE_NAME} — không có câu hỏi nào đang chờ.` };
  }

  const question = interview.questions.find((q) => q.id === input.questionId);
  if (question === undefined) {
    return {
      ok: false,
      reason: `không có câu hỏi id "${input.questionId}" — trả lời đúng id đã hỏi.`,
    };
  }

  let values = Array.isArray(input.answer) ? input.answer : [input.answer];
  if (question.type !== "text") {
    // V2-1 (E3): câu có `option_ids` ⇒ id, hoặc nhãn duy nhất chuẩn hoá về id.
    const resolved = resolveAnswerValues(question, values);
    if (!resolved.ok) return { ok: false, reason: resolved.reason };
    values = resolved.values;
  }
  if (question.type === "single" && values.length !== 1) {
    return { ok: false, reason: `câu "${question.id}" type single chỉ nhận đúng một giá trị.` };
  }
  const stored: string | string[] = Array.isArray(input.answer) ? values : (values[0] as string);

  const next: Interview = {
    ...interview,
    answers: {
      ...interview.answers,
      [question.id]: {
        value: stored,
        answered_at: now().toISOString(),
        answered_by: input.answeredBy,
      },
    },
  };
  await writeInterview(input.controlRoot, InterviewSchema.parse(next));
  return { ok: true };
}
